'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Default every Date / Intl date-formatting call to Mountain Time
 * (America/Edmonton) when no explicit `timeZone` is given.
 *
 * The `TZ` env var pins server-side formatting, but the browser ignores it,
 * so a client-rendered date would otherwise show in the visitor's local
 * timezone. Patching the formatting primitives ONCE here makes every
 * `toLocale*` / `Intl.DateTimeFormat` call across the app resolve to
 * Mountain Time without each call site passing `timeZone` itself. Calls that
 * DO pass a `timeZone` are left untouched. See CONVENTIONS.md › Timezone.
 *
 * Runs on both server and client (idempotent) so SSR and hydration agree.
 */

const TIMEZONE = 'America/Edmonton';

function installMountainTimeDefault(): void {
  const g = globalThis as any;
  if (g.__mtTimezonePatched) return;
  g.__mtTimezonePatched = true;

  // Keep an explicit timeZone if the caller already set one; otherwise pin
  // it to Mountain Time.
  const withTimeZone = (options: any) =>
    options && options.timeZone ? options : { ...options, timeZone: TIMEZONE };

  // Date.prototype.toLocale* do NOT route through the global
  // Intl.DateTimeFormat binding, so patch each method directly.
  const proto = Date.prototype as any;
  for (const method of ['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString'] as const) {
    const original = proto[method];
    proto[method] = function (this: Date, locales?: any, options?: any) {
      return original.call(this, locales, withTimeZone(options));
    };
  }

  // Intl.DateTimeFormat, patch the constructor for code that uses it directly.
  const intl = Intl as any;
  const Original = intl.DateTimeFormat;
  const Patched: any = function (locales?: any, options?: any) {
    return new Original(locales, withTimeZone(options));
  };
  Patched.prototype = Original.prototype;
  Patched.supportedLocalesOf = Original.supportedLocalesOf;
  intl.DateTimeFormat = Patched;
}

installMountainTimeDefault();

/** Renders nothing, importing/mounting it installs the Mountain Time default. */
export default function TimezoneDefault(): null {
  return null;
}
